import { Date_dateOnly } from "../../common/dateUtils";
import { CodeStatus, Location, PermitType, SignatureType, WorkOrigin } from "../../common/enums";
import positionUtils from "../../common/positionUtils";
import { formElement } from "../forms/FormElement";
import EnergyHarbourPermitFormState, {
    EnergyHarbourPermitFormState_default
} from "../forms/Permits/EnergyHarbourPermit/EnergyHarbourPermitFormState";
import UnderWaterWorkPermitFormState from "../forms/Permits/UnderWaterWorkPermitFormState";
import { PersonFormElement_default } from "../forms/PersonFormElement";
import Person, { Person_default, Person_toFormElement, Person_toParticipantListItem } from "../Person";
import Position, { Position_toPositionPickerRow } from "../Position";
import Signature, { Signature_normalize } from "../Signature";
import { permitContentVersions } from "../WorkPermit";

type UnderWaterWorkPermit = {
    remindMe: boolean;
    remindMeDate: Date;
    id: string;
    entityId: number;
    startDate: Date;
    endDate: Date;
    description: string;
    status: CodeStatus;
    positions: Position[];
    sequenceIdentifier: string;
    persons: Person[];
    permitName: PermitType.UnderWaterWorkPermit;
    createdByUserName: string;
    expirationReminderEmail: string | undefined;
    expirationReminderPhoneNumber: string | undefined;
    version: number;
    hasAttachedFiles: boolean;
    entrepreneur: string;
    renewalOfPermitSequenceIdentifier: string;
    initialStartDate: Date;
    renewedByPermitSequenceIdentifier: string;
    extendedEndDate: Date;
    responsibleWorkLeader: Person;
    projectLeader: Person;
    companyOrgNr: string;
    vhfIsAvailable: boolean;
    workOrigin: WorkOrigin;
    workLocation: Location;
    departmentId: number;
    rejectionComment: string;
    adminComment: string;
    energyHarbourSequenceIdentifier: string;

    permitProviderSignature: Signature;

    contentVersion: number;

    canUpdate: boolean;
    canAccept: boolean;
    canCheckout: boolean;
    canRenew: boolean;
    canManageAttachments: boolean;
    canManagePositionActiveStatus: boolean;
    canManagePresentParticipants: boolean;
    canManageAdminComment: boolean;
    canManagePositions: boolean;
    canManageParticipants: boolean;
    canPrefillEnergyHarbourPermit: boolean;
    canManagePositionPolygons: boolean;
};

export default UnderWaterWorkPermit;

export function UnderWaterWorKPermit_normalize(permit: UnderWaterWorkPermit): UnderWaterWorkPermit {
    return {
        adminComment: permit.adminComment ?? "",
        companyOrgNr: permit.companyOrgNr ?? "",
        canAccept: permit.canAccept ?? false,
        canCheckout: permit.canCheckout ?? false,
        canManageAdminComment: permit.canManageAdminComment ?? false,
        canManageAttachments: permit.canManageAttachments ?? false,
        canManageParticipants: permit.canManageParticipants ?? false,
        canManagePositionActiveStatus: permit.canManagePositionActiveStatus ?? false,
        canManagePositions: permit.canManagePositions ?? false,
        canManagePresentParticipants: permit.canManagePresentParticipants ?? false,
        canPrefillEnergyHarbourPermit: permit.canPrefillEnergyHarbourPermit ?? false,
        canRenew: permit.canRenew ?? false,
        canUpdate: permit.canUpdate ?? false,
        createdByUserName: permit.createdByUserName ?? "",
        departmentId: permit.departmentId ?? 0,
        description: permit.description ?? "",
        entityId: permit.entityId ?? 0,
        energyHarbourSequenceIdentifier: permit.energyHarbourSequenceIdentifier ?? "",
        entrepreneur: permit.entrepreneur ?? "",
        expirationReminderEmail: permit.expirationReminderEmail ?? "",
        expirationReminderPhoneNumber: permit.expirationReminderPhoneNumber ?? "",
        hasAttachedFiles: permit.hasAttachedFiles ?? false,
        id: permit.id ?? "",
        permitName: PermitType.UnderWaterWorkPermit,
        persons:
            (!permit.persons || permit.persons.length === 0) && permit.status === CodeStatus.Registered
                ? [PersonFormElement_default()]
                : (permit.persons ?? []),
        positions: permit.positions.sort((a, b) => a.order - b.order),
        remindMeDate: new Date(permit.remindMeDate),
        startDate: new Date(permit.startDate),
        endDate: new Date(permit.endDate),
        initialStartDate: new Date(permit.initialStartDate),
        extendedEndDate: new Date(permit.extendedEndDate),
        permitProviderSignature: Signature_normalize(permit.permitProviderSignature, SignatureType.PermitProvider),
        projectLeader: permit.projectLeader ?? PersonFormElement_default(),
        renewalOfPermitSequenceIdentifier: permit.renewalOfPermitSequenceIdentifier ?? "",
        rejectionComment: permit.rejectionComment ?? "",
        remindMe: permit.remindMe ?? false,
        renewedByPermitSequenceIdentifier: permit.renewedByPermitSequenceIdentifier ?? "",
        responsibleWorkLeader: permit.responsibleWorkLeader ?? PersonFormElement_default(),
        sequenceIdentifier: permit.sequenceIdentifier ?? "",
        status: permit.status ?? CodeStatus.Registered,
        version: permit.version ?? 0,
        vhfIsAvailable: permit.vhfIsAvailable ?? false,
        workLocation: permit.workLocation ?? Location.General,
        workOrigin: permit.workOrigin ?? WorkOrigin.Quay,
        canManagePositionPolygons: permit.canManagePositionPolygons ?? false,
        contentVersion: permit.contentVersion ?? Math.max(...Object.values(permitContentVersions))
    };
}

export function UnderWaterWorkPermit_toFormState(permit: UnderWaterWorkPermit): UnderWaterWorkPermitFormState {
    const formState: UnderWaterWorkPermitFormState = {
        adminComment: formElement(permit.adminComment),
        description: formElement(permit.description),
        endDate: formElement(permit.endDate),
        entrepreneur: formElement(permit.entrepreneur),
        expirationReminderEmail: formElement(permit.expirationReminderEmail ?? ""),
        expirationReminderPhoneNumber: formElement(permit.expirationReminderPhoneNumber ?? ""),
        positions: permit.positions.map(Position_toPositionPickerRow),
        remindMe: formElement(permit.remindMe),
        companyOrgNr: formElement(permit.companyOrgNr),
        persons: permit.persons.map(Person_toParticipantListItem),
        projectLeader: Person_toFormElement(permit.projectLeader),
        responsibleWorkLeader: Person_toFormElement(permit.responsibleWorkLeader),
        remindMeDate: formElement(permit.remindMeDate),
        startDate: formElement(permit.startDate),
        vhfIsAvailable: formElement(permit.vhfIsAvailable),
        workLocation: formElement(permit.workLocation),
        workOrigin: formElement(permit.workOrigin),
        energyHarbourSequenceIdentifier: formElement(permit.energyHarbourSequenceIdentifier)
    };

    return formState;
}

export function UnderWaterWorkPermit_toEnergyHarbourPermitFormState(
    permit: UnderWaterWorkPermit
): EnergyHarbourPermitFormState {
    const prefilledFields = {
        remindMe: formElement(permit.remindMe),
        remindMeDate: formElement(permit.remindMeDate),
        startDate: formElement(Date_dateOnly(permit.startDate)),
        endDate: formElement(Date_dateOnly(permit.endDate)),
        description: formElement(permit.description),
        entrepreneur: formElement(permit.entrepreneur),
        positions: permit.positions.map((p) => {
            const positionPickerRow = Position_toPositionPickerRow(p);
            positionPickerRow.id = 0;
            positionUtils.addPositionOffset(positionPickerRow);

            return positionPickerRow;
        }),
        expirationReminderEmail: formElement(permit.expirationReminderEmail),
        expirationReminderPhoneNumber: formElement(permit.expirationReminderPhoneNumber),
        RiskIdentifications: {},
        workLeader: Person_toFormElement(permit.responsibleWorkLeader),
        persons: permit.persons.map(Person_toParticipantListItem)
    };

    const defaultFormState = EnergyHarbourPermitFormState_default();

    const energyHarbourPermitFormState: EnergyHarbourPermitFormState = {
        ...defaultFormState,
        riskIdentifications: {
            ...defaultFormState.riskIdentifications,
            isRaftIncludedInPrint: formElement(true),
            raftChecklist: {
                ...defaultFormState.riskIdentifications.raftChecklist,
                underWaterWorkPermitSequenceIdentifier: formElement(permit.sequenceIdentifier)
            }
        },
        ...prefilledFields
    };

    return energyHarbourPermitFormState;
}
